import React, {ReactNode, useState} from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  FormControl,
  FormLabel,
  Select,
} from '@chakra-ui/react';

import {addDays} from 'date-fns';
import {Spanish} from 'flatpickr/dist/l10n/es.js';
import Flatpickr from 'react-flatpickr';
import {useParams} from 'react-router';

import {useSelectedCountry} from '../contexts/CountryContext';
import {useAccessibleProjects} from '../hooks/useAccessibleProjects';
import {useCountryFilteredUsers} from '../hooks/useCountryFilteredUsers';
import {UserList} from '../pages/schedule/UserList';
import {CalendarFilter} from '../slices/calendarFilterSlice';
import {Project} from '../types/projects';
import {TourFilter} from '../types/tours';
import {FiltersUsers, User} from '../types/users';
import {sortAlpha} from '../utils/sortAlpha';

interface SelectFiltersProps {
  isLoading: boolean;
  users: User[];
  listUsers: FiltersUsers[];
  selectProject: string;
  selectDate?: Date[] | null;
  onChangeProject: (pro: string) => void;
  onChangeDate?: (date: Date[] | null) => void;
  onChangeSelectedUser: (user: FiltersUsers, parent: string[]) => void;
  userSelected: CalendarFilter[] | TourFilter[];
  wTab?: number;
  typeDate: string;
  /** Contenido opcional al inicio de cada fila; lo decide la vista. */
  renderRowAccessory?: (user: FiltersUsers) => ReactNode;
  /** Filtra el listado por el país global (default true). */
  filterByCountry?: boolean;
}

export const SelectFilters = (props: SelectFiltersProps) => {
  const {
    isLoading,
    users,
    listUsers,
    selectProject,
    selectDate,
    onChangeProject,
    onChangeDate,
    onChangeSelectedUser,
    userSelected,
    wTab,
    typeDate,
    filterByCountry = true,
    renderRowAccessory,
  } = props;
  const {idOrganization} = useParams();
  const {data: projects} = useAccessibleProjects(idOrganization);
  const {selectedCountry} = useSelectedCountry();

  const scopedUsers = useCountryFilteredUsers(listUsers, filterByCountry);

  // El drill/búsqueda del picker se reinicia con el contexto.
  const pickerResetKey = filterByCountry
    ? `${selectProject ?? ''}|${selectedCountry?.id ?? ''}`
    : selectProject;

  const listProject = sortAlpha(projects, 'name');

  const [minDateRange, setMinDateRange] = useState<Date | null>(null);
  const [maxDateRange, setMaxDateRange] = useState<Date | null>(null);

  const onChangeDateRange = (date: Date[]) => {
    if (date.length <= 1) {
      return;
    }
    if (typeDate === 'range') {
      setMinDateRange(date[0]);
      if (date[0] < new Date()) {
        setMaxDateRange(addDays(date[0], 6));
        if (onChangeDate) {
          onChangeDate(date);
        }
      }
    }
  };

  return (
    <Accordion defaultIndex={[0, 1]} allowMultiple w='max-content' maxW='280px'>
      <AccordionItem bg='gray.50' borderTopRadius='18px' borderTopWidth='none'>
        <h2>
          <AccordionButton
            _focus={{boxShadow: 'none'}}
            _focusVisible={{
              boxShadow: 'outline',
              position: 'relative',
              zIndex: 1,
            }}>
            <Box flex='1' textAlign='left' fontWeight='bold'>
              Filtros
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={0} px={2}>
          <Box bg='gray.50' h='auto' p={4}>
            {selectDate && (
              <FormControl mt={-5} mb={3}>
                <FormLabel fontSize={14}>Fecha:</FormLabel>
                <Flatpickr
                  name='dates'
                  options={{
                    mode: typeDate,
                    locale: Spanish,
                    minDate: minDateRange,
                    maxDate: maxDateRange,
                    disableMobile: 'true',
                  }}
                  placeholder='Seleccione una fecha'
                  value={selectDate}
                  onOpen={() => {
                    setMinDateRange(null);
                    setMaxDateRange(null);
                  }}
                  onChange={onChangeDateRange}
                />
              </FormControl>
            )}
            <FormControl mt={selectDate ? 3 : -5} mb={-2}>
              <FormLabel fontSize={14}>Proyecto</FormLabel>
              <Select
                size='sm'
                value={selectProject}
                placeholder='Seleccione...'
                onChange={e => {
                  onChangeProject(e.target.value);
                }}>
                {listProject?.map((project: Project) => (
                  <option key={project.idProject} value={project.idProject}>
                    {project.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Box>
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem
        bg='gray.50'
        borderBottomRadius='18px'
        borderTopWidth='none'
        position='relative'
        border='none'>
        <h2>
          <AccordionButton
            _focus={{boxShadow: 'none'}}
            _focusVisible={{
              boxShadow: 'outline',
              position: 'relative',
              zIndex: 1,
            }}>
            <Box
              flex='1'
              textAlign='left'
              fontWeight='bold'
              w={wTab ? wTab : '240px'}>
              Usuarios
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={1} pt={0} px={2}>
          <UserList
            users={users}
            listUsers={scopedUsers}
            isLoading={isLoading}
            onChangeUser={onChangeSelectedUser}
            userSelected={userSelected}
            selectProject={selectProject}
            resetKey={pickerResetKey}
            renderRowAccessory={renderRowAccessory}
          />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
